export const DEFAULT_COMMENT_PLACEHOLDER =
  'Please tell us in 1 or 2 sentences why you would like to return this item.';

export const DEFAULT_PHOTO_UPLOAD_PLACEHOLDER = `<span style='text-decoration: underline; color: #2B7CAC'>Click to select files</span> or drag & drop files here. <br/> Must be smaller than 10MB.<br/> Must be a PNG, JPG or HEIF.`;

export const DEFAULT_PHOTO_UPLOAD_TITLE = 'Upload a photo of your item';

export const PHOTO_UPLOAD_MAX_FILES_OPTIONS = [{ value: 1 }, { value: 2 }];

export const ENGLISH_LANG_CODE = 'en';

export const QTY_WITH_EXPIRY_INPUT = 'qty_with_expiry_input';

// The editor offers the claim product type to a retailer that has the assist product or the
// shipping-protection product. The GrowthBook flag must also be on. The editor enforces both
// conditions.
export const CLAIM_PRODUCT_TYPE = 'claim';
export const RETURN_PRODUCT_TYPE = 'return';
export const EXCHANGE_PRODUCT_TYPE = 'exchange';

export const CLAIM_REASONS_FLAG = 'hub-configurable-claim-reasons';

// Retailer product-entitlement keys, read from retailerSettings.products[]. Claim authoring is offered
// to a retailer with the assist product or the shipping-protection product. These match the
// return-services serving gate (ClaimsHelper.isRetailerEnabledOnAssist keys off "assist";
// isRetailerEnabledForShippingProtection keys off "shipping_protection").
export const ASSIST_PRODUCT_KEY = 'assist';
export const SHIPPING_PROTECTION_PRODUCT_KEY = 'shipping_protection';

// On-selection behavior compared to resolver
// -------------------------------------------
// A claim reason's on-selection behavior is what the reason does when a shopper selects it: proceed
// to a claim, redirect to an external destination, or end with a terminal message. Behavior is a
// client-only editing concept — it decides which editor this app renders. It is NOT stored on the
// wire; return-services derives the shopper's screen from the resolver.
//
// A resolver is one implementation of a behavior. The resolver owns the seed defaults (name and
// copy) and the editor presentation for the reason it creates. "Seel" implements external-redirect;
// the terminal-message resolver implements terminal-message. The resolver id is the wire
// discriminator: a reason stores `reason_resolver.type`, and return-services dispatches on it. A
// second redirect target or a different terminal preset is a new entry in REASON_RESOLVERS. R1 has
// exactly one resolver per behavior.
export const ON_SELECTION_PROCEED_TO_CLAIM = 'proceed_to_claim';
export const ON_SELECTION_EXTERNAL_REDIRECT = 'external_redirect';
export const ON_SELECTION_TERMINAL_MESSAGE = 'terminal_message';

export const RESOLVER_CLAIM = 'claim';
export const RESOLVER_SEEL = 'seel';
export const RESOLVER_TERMINAL_MESSAGE = 'terminal_message';

// Seed defaults owned by the Seel resolver, its implementation of the external-redirect behavior.
// The author can edit the copy after the reason uses these defaults.
export const SEEL_RESOLVER_DEFAULTS = {
  name: 'I have a shipping issue',
  copy: {
    heading: "You're being redirected to Seel",
    body:
      "Shipping issues for $retailerName orders are covered by Seel, our shipping insurance provider. You'll be taken to resolve.seel.com to file and track your claim.",
    redirect_text: 'Continue to Seel',
    back_text: 'Go back',
  },
};

// Seed defaults owned by the terminal-message resolver.
export const TERMINAL_MESSAGE_RESOLVER_DEFAULTS = {
  name: 'Something else',
  copy: {
    heading: 'Let us help you directly',
    body:
      'For anything other than a shipping issue, our $retailerName support team can help.',
  },
};

// The resolver registry. It lists the implementations the editor offers, in dropdown and menu
// order. Each `behavior` is the on-selection behavior that resolver implements. Each `defaults` is
// the seed config that resolver owns.
export const REASON_RESOLVERS = [
  {
    id: RESOLVER_CLAIM,
    label: 'Claim',
    menuLabel: 'Claim reason',
    behavior: ON_SELECTION_PROCEED_TO_CLAIM,
  },
  {
    id: RESOLVER_SEEL,
    label: 'Seel redirect',
    menuLabel: 'Seel redirect',
    behavior: ON_SELECTION_EXTERNAL_REDIRECT,
    defaults: SEEL_RESOLVER_DEFAULTS,
    editor: {
      sectionTitle: 'Seel Redirect',
      sectionHint:
        "Shoppers see this confirmation before leaving. The destination is the per-order Seel deep link from the delivery-protection integration — it isn't configured here. Copy is prefilled with the Narvar defaults.",
    },
  },
  {
    id: RESOLVER_TERMINAL_MESSAGE,
    label: 'Support message',
    menuLabel: 'Support contact message',
    behavior: ON_SELECTION_TERMINAL_MESSAGE,
    defaults: TERMINAL_MESSAGE_RESOLVER_DEFAULTS,
    editor: {
      sectionTitle: 'Support Contact Message',
      sectionHint:
        'Ends the flow with your support contacts — no claim, no cart. Copy is prefilled with the Narvar defaults.',
    },
  },
];

export const resolverById = id => REASON_RESOLVERS.find(r => r.id === id);

// The resolver that implements a given behavior. R1 has exactly one resolver for each behavior, so
// the result is unambiguous. If a behavior gains a second resolver, each reason must store its
// resolver id, and callers must use `resolverById` instead of the behavior.
export const resolverForBehavior = behavior =>
  REASON_RESOLVERS.find(r => r.behavior === behavior);

// Resolvers reachable from the split "Add claim reason" caret. This is every resolver except the
// primary Claim.
export const ADDABLE_SECONDARY_RESOLVERS = REASON_RESOLVERS.filter(
  r => r.id !== RESOLVER_CLAIM,
);

// Card-header kind selector options, one for each resolver. Each `key` is the behavior, which drives
// the editor this app renders. Each `resolverId` is the implementation the author selected and is
// what gets stored as `reason_resolver.type`.
export const ON_SELECTION_BEHAVIOR_OPTIONS = REASON_RESOLVERS.map(r => ({
  key: r.behavior,
  description: r.label,
  resolverId: r.id,
}));

// The support contact methods a terminal-message reason can show. The retailer authors the label
// and the link text. The href destination is a fixed client-side token. The author does not edit
// it in the UI.
export const TERMINAL_CONTACT_METHODS = [
  {
    method: 'phone',
    title: 'Phone',
    label: 'Call us',
    link: '$support_contact.phone',
    href: 'tel:$support_contact.phone',
  },
  {
    method: 'email',
    title: 'Email',
    label: 'Email us',
    link: '$support_contact.email',
    href: 'mailto:$support_contact.email',
  },
  {
    method: 'web',
    title: 'Web / FAQ',
    label: 'Need more info?',
    link: 'Frequently Asked Questions',
    href: '$support_contact.web',
  },
];

// Claim types that have no item to return, so a physical return does not apply to them.
export const NON_RETURNABLE_CLAIM_TYPES = ['PackageMissing', 'ItemMissing'];

export const isNonReturnableClaimType = claimType =>
  NON_RETURNABLE_CLAIM_TYPES.includes(claimType);

// Seed defaults for a new proceed-to-claim reason.
export const DEFAULT_CLAIM_CLASSIFICATION = {
  claim_type: 'WrongItem',
  detailed_reason: 'ReceivedDifferentProduct',
};

export const ENTERPRISE_QUANTITY_FIELDS = {
  quantity_groupings_enabled: {
    type: 'qty_input',
    validate: false,
    enumerable: false,
  },
  expiration_date_enabled: {
    type: QTY_WITH_EXPIRY_INPUT,
    validate: true,
    enumerable: true,
  },
};
